import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';
import {
  BuildFieldPathDialogComponent,
  BuildFieldPathDialogResult,
} from '../dialogs/build-field-path-dialog.component';
import {
  buildPathModelFromTemplatePath,
  pathModelHasRepeatableStep,
} from '../xml/xml-path-step-builder';
// import {
//   isXmlFieldPathReadyForSave,
//   needsXmlPathContextForSave,
// } from '../utils/routing-xml-field-path-validation.util';
import type { XmlPathNodePredicate } from '../xml/xml-field-key-map.flatten';
import type { RoutingPipelineBlock } from '../models/routing-ruleconfig1.models';
import { xmlFieldOptionDisplayLabel, type XmlFieldMapMeta } from '../models/field-option.model';
import { XmlFieldCatalogService } from './xml-field-catalog.service';
import { XmlFieldPathResolverService } from './xml-field-path-resolver.service';
import {
  getRuleNameValidationMessage,
  RULE_NAME_ALREADY_EXISTS_MESSAGE,
  RuleEngineService,
} from '../../services/rule-engine.service';
import { RoutingRuleconfig1StoreService } from './routing-ruleconfig1-store.service';
import { routingExpressionPrep } from './routing-ruleconfig1-expression-prep.util';

@Injectable()
export class RoutingRuleconfig1XmlRulesService {
  constructor(
    private readonly store: RoutingRuleconfig1StoreService,
    private readonly catalog: XmlFieldCatalogService,
    private readonly pathResolver: XmlFieldPathResolverService,
    private readonly dialog: MatDialog,
    private readonly toastr: ToastrService,
    private readonly ruleEngineService: RuleEngineService
  ) {}

  /**
   * Converts a persisted XML field path to the display label shown in the rule form.
   * @example `toFieldDisplay('Case/Report/ObservationDetails[@Type=\'TC\']/Observation[2]/@Value')` → `'Observation Value'`
   */
  toFieldDisplay(field: string): string {
    return this.pathResolver.displayKeyFromPersistedPath(field);
  }

  /**
   * Resolves the display label or map key from the form to the path stored on the rule.
   * @example `toPersistedField('Observation Value')` → `'Case/.../Observation/@Value'` (from catalog or `fieldDisplayToPath`)
   */
  toPersistedField(displayLabel: string): string {
    const label = (displayLabel ?? '').toString().trim();
    if (!label) {
      return '';
    }
    const fromMap = this.store.fieldDisplayToPath.get(label);
    if (fromMap) {
      return fromMap.trim();
    }
    const opt = this.catalog.findByKey(label) ?? this.catalog.findByLabel(label);
    return (opt?.path ?? label).trim();
  }

  /**
   * Whether the current field selection needs Build Path (repeatable nodes or predicates).
   * @example field with `repeatableNodes: ['Observation']` and no indexed draft → `true`
   */
  showBuildPathButton(): boolean {
    const label = (this.store.fieldControl.value ?? '').toString().trim();
    if (!label) {
      return false;
    }
    const meta = this.getMetaForLabel(label);
    if (!meta?.path) {
      return false;
    }
    if ((meta.nodePredicates?.length ?? 0) > 0) {
      return true;
    }
    if ((meta.repeatableNodes?.length ?? 0) > 0) {
      return true;
    }
    if (meta.isRepeatable) {
      return true;
    }
    const model = buildPathModelFromTemplatePath(meta.path);
    return !!(model && pathModelHasRepeatableStep(model));
  }

  /**
   * Opens the Build Path dialog for the field currently in `fieldControl`, when applicable.
   * @example user clicks Build Path on a repeatable Observation field → dialog with occurrence rows
   */
  openBuildPathForCurrentField(): void {
    const label = (this.store.fieldControl.value ?? '').toString().trim();
    const meta = this.getMetaForLabel(label);
    if (!meta?.path) {
      return;
    }
    if (this.tryOpenXmlBuildPathDialog(label, meta)) {
      return;
    }
  }

  /**
   * Opens Build Path when the field has repeatable nodes or repeatable steps in its template.
   * @example `Observation/@Value` with repeatableNodes `['ObservationDetails']` → opens dialog, returns true
   */
  private tryOpenXmlBuildPathDialog(fieldLabel: string, meta: XmlFieldMapMeta): boolean {
    const templatePath = (meta.path ?? '').trim();
    const repeatableNodes = meta.repeatableNodes ?? [];
    if (repeatableNodes.length > 0) {
      this.openXmlBuildFieldPathDialog(fieldLabel, templatePath, meta.nodePredicates ?? []);
      return true;
    }
    const pathBuild = buildPathModelFromTemplatePath(templatePath);
    if (pathBuild && pathModelHasRepeatableStep(pathBuild)) {
      this.openXmlBuildFieldPathDialog(fieldLabel, templatePath, meta.nodePredicates ?? []);
      return true;
    }
    return false;
  }

  /**
   * Opens BuildFieldPathDialog for a repeatable XML field and stores the chosen contextual path.
   * @example template `Case/ObservationDetails/Observation/@Value` → user picks index → `rememberFieldPath`
   */
  private openXmlBuildFieldPathDialog(
    fieldLabel: string,
    templatePath: string,
    nodePredicates: XmlPathNodePredicate[]
  ): void {
    const pathBuild = buildPathModelFromTemplatePath(templatePath);
    if (!pathBuild || !pathModelHasRepeatableStep(pathBuild)) {
      return;
    }
    const basePath =
      this.store.fieldDisplayToPath.get(fieldLabel) ??
      this.catalog.findByLabel(fieldLabel)?.path ??
      templatePath;
    this.dialog
      .open(BuildFieldPathDialogComponent, {
        width: '640px',
        maxWidth: '96vw',
        data: {
          fieldLabel,
          model: pathBuild,
          initialPath: basePath.trim(),
          nodePredicateSpecs: (nodePredicates ?? []).map((p) => ({
            node: p.node,
            attribute: p.attribute,
            value: '',
          })),
        },
        disableClose: true,
        autoFocus: false,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: BuildFieldPathDialogResult | undefined) => {
        if (!result?.path?.trim()) {
          return;
        }
        this.rememberFieldPath(fieldLabel, result.path.trim());
      });
  }

  /**
   * Handles autocomplete selection: sets display key, remembers template path, may auto-open Build Path.
   * @example `onFieldOptionSelected('Case/Report/.../Observation/@Value')` → updates control and `fieldDisplayToPath`
   */
  onFieldOptionSelected(path: string): void {
    const opt = this.catalog.findByPath(path);
    if (!opt) {
      return;
    }
    const displayKey = xmlFieldOptionDisplayLabel(opt);
    this.rememberFieldPath(displayKey, opt.path);
    this.store.fieldControl.setValue(displayKey);
    if (this.shouldAutoOpenBuildPath({ ...opt, nodePredicates: opt.nodePredicates })) {
      setTimeout(() => this.openBuildPathForCurrentField(), 0);
    }
  }

  /**
   * Validates the rule form and appends or updates a rule on the target pipeline block.
   * @example add rule: field `Accession Number`, operator `equals`, value `123` → new row on selected block
   */
  saveAddOrUpdate(): void {
    if (this.warnAndStopOnInvalidRuleName()) {
      return;
    }
    if (this.store.ruleForm.invalid) {
      return;
    }
    const block = this.getTargetBlockForSave();
    if (!block) {
      this.toastr.warning('Select or add a pipeline first.', '', {
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      });
      return;
    }
    const fieldLabel = (this.store.fieldControl.value ?? '').toString().trim();
    // COMMENTED OUT: allow manual XML field paths not present in catalog autocomplete.
    // if (
    //   fieldLabel &&
    //   !this.catalog.findByKey(fieldLabel) &&
    //   !this.catalog.findByLabel(fieldLabel) &&
    //   !this.store.fieldDisplayToPath.has(fieldLabel)
    // ) {
    //   this.toastr.error('Choose a known field from the suggestions list.', '', {
    //     timeOut: 4500,
    //     positionClass: 'toast-bottom-right',
    //   });
    //   return;
    // }
    // const meta = this.getMetaForLabel(fieldLabel);
    // COMMENTED OUT: allow save with manually entered paths without forcing Build Path first.
    // if (meta && needsXmlPathContextForSave(meta)) {
    //   const draft = this.store.fieldDisplayToPath.get(fieldLabel) ?? meta.path ?? '';
    //   if (!isXmlFieldPathReadyForSave(meta.path, draft, meta)) {
    //     this.toastr.warning(
    //       'Use Build Path to set occurrence on this repeatable field before save.',
    //       '',
    //       {
    //         timeOut: 6000,
    //         positionClass: 'toast-bottom-right',
    //       }
    //     );
    //     return;
    //   }
    // }
    const field = this.toPersistedField(fieldLabel);
    if (!field) {
      return;
    }
    const op = this.store.mapOperatorToUi(this.store.ruleForm.get('operator')?.value);
    const ruleName = this.store.getRuleNameInputValue();
    // const value = (this.store.ruleForm.get('value')?.value ?? '')
    //   .toString()
    //   .replace(/\s*\|\s*/g, '|')
    //   .trim();
    const value = this.ruleEngineService.normalizeRuleValue(
      this.store.ruleForm.get('value')?.value ?? ''
    );

    const excludeRuleIndex = this.store.isEditingAnyRule() ? this.store.editingRuleIndex : null;
    if (this.store.isDuplicateRuleNameInBlock(block, ruleName, excludeRuleIndex)) {
      this.toastr.warning(RULE_NAME_ALREADY_EXISTS_MESSAGE, '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right',
      });
      return;
    }

    if (this.store.isEditingAnyRule() && this.store.editingRuleIndex !== null) {
      const r = block.rules[this.store.editingRuleIndex];
      if (r) {
        r.rulename = ruleName;
        r.field = field;
        r.value = value;
        r.operation = op;
      }
    } else {
      const nextNo = block.rules.length + 1;
      block.rules.push({
        rulenumber: nextNo,
        rulename: ruleName || `Rule${nextNo}`,
        field,
        value,
        operation: op,
        delimiter: '|',
      });
      routingExpressionPrep.seedRuleSatisfactionIfFirstRule(block);
    }
    this.clearEditForm();
    this.store.patchBlocks([...this.store.pipelineBlocks]);
  }

  /**
   * Loads an existing rule into the form for editing and locks pipeline selection.
   * @example `startEditRule('pipeline-1', 0)` → patches rulename, field display, operator, value from `block.rules[0]`
   */
  startEditRule(blockId: string, index: number): void {
    const block = this.store.pipelineBlocks.find((b) => b.id === blockId);
    const rule = block?.rules[index];
    if (!rule) {
      return;
    }
    this.store.editingBlockId = blockId;
    this.store.editingRuleIndex = index;
    this.store.isPipelineLocked = true;
    const persisted = (rule.field ?? '').toString();
    const display = this.toFieldDisplay(persisted);
    if (display && persisted) {
      this.store.fieldDisplayToPath.set(display, persisted);
    }
    this.store.ruleForm.patchValue({
      pipeline: blockId,
      rulename: rule.rulename ?? '',
      field: display,
      operator: this.store.mapOperatorToUi(rule.operation ?? ''),
      value: this.ruleEngineService.normalizeRuleValue(rule.value ?? ''),
    });
    this.store.setRuleNameFieldDisabled(
      this.ruleEngineService.isRuleInBlockBuildExpression(rule, block)
    );
  }

  /**
   * Clears edit state and resets the rule form while keeping the selected pipeline.
   * @example after save or cancel edit → `editingRuleIndex` null, form fields empty except pipeline
   */
  clearEditForm(): void {
    this.store.editingBlockId = null;
    this.store.editingRuleIndex = null;
    this.store.isPipelineLocked = false;
    this.store.setRuleNameFieldDisabled(false);
    const keepPipeline = (this.store.selectedPipelineId ?? '').toString();
    this.store.ruleForm.reset(
      {
        pipeline: keepPipeline,
        rulename: '',
        field: '',
        operator: '',
        value: '',
      },
      { emitEvent: false }
    );
    this.store.ruleForm.markAsPristine();
    this.store.ruleForm.markAsUntouched();
  }

  /**
   * Resolves catalog metadata for a field display label, stored path, or map key.
   * @example `getMetaForLabel('Grossing.Observation.Value')` → meta with repeatableNodes
   */
  private getMetaForLabel(label: string): XmlFieldMapMeta | null {
    const k = (label ?? '').toString().trim();
    if (!k) {
      return null;
    }
    const storedPath = this.store.fieldDisplayToPath.get(k)?.trim();
    if (storedPath) {
      const fromStored = this.pathResolver.metaForPersistedPath(storedPath);
      if (fromStored) {
        return fromStored;
      }
    }
    if (k.includes('/')) {
      const fromPathLabel = this.pathResolver.metaForPersistedPath(k);
      if (fromPathLabel) {
        return fromPathLabel;
      }
    }
    const byKey = this.catalog.getMetaByKey(k);
    if (byKey) {
      return byKey;
    }
    const opt = this.catalog.findByKey(k) ?? this.catalog.findByLabel(k);
    if (opt) {
      return (
        this.catalog.getMetaByKey(opt.key) ?? {
          path: opt.path,
          isRepeatable: opt.isRepeatable,
          repeatableNodes: opt.repeatableNodes,
          nodePredicates: opt.nodePredicates,
        }
      );
    }
    return null;
  }

  /**
   * Resolves the pipeline block for add/edit: editing target or selected/new pipeline from form.
   * @example selectedPipelineId `'pipe-1'` → existing block; missing id → creates empty block
   */
  private getTargetBlockForSave(): RoutingPipelineBlock | null {
    if (this.store.editingBlockId !== null && this.store.editingRuleIndex !== null) {
      return this.store.pipelineBlocks.find((b) => b.id === this.store.editingBlockId) ?? null;
    }
    const pipelineId = (
      this.store.selectedPipelineId ??
      ((this.store.ruleForm.get('pipeline')?.value ?? '') || '').toString()
    )
      .toString()
      .trim();
    if (!pipelineId) {
      return null;
    }
    let block = this.store.pipelineBlocks.find((b) => b.id === pipelineId);
    if (!block) {
      block = {
        id: pipelineId,
        label: this.store.pipelineIdToDisplayName.get(pipelineId) || pipelineId,
        rules: [],
        ruleSatisfaction: '1',
        expanded: true,
      };
      this.store.pipelineBlocks.push(block);
    }
    return block;
  }

  /**
   * Caches display label → persisted XPath after Build Path or autocomplete selection.
   * @example `rememberFieldPath('Grossing.Observation.Value', 'Case/ObservationDetails[@SubType=\'Grossing\']/...')`
   */
  private rememberFieldPath(fieldLabel: string, path: string): void {
    const p = (path ?? '').toString().trim();
    const label = (fieldLabel ?? '').toString().trim();
    if (!label || !p) {
      return;
    }
    this.store.fieldDisplayToPath.set(label, p);
    const opt = this.catalog.findByLabel(label);
    if (opt?.key && opt.key !== label) {
      this.store.fieldDisplayToPath.set(opt.key, p);
    }
  }

  /**
   * Returns true when picking a field should immediately open Build Path (repeatable / predicates).
   * @example field with nodePredicates `[{ node: 'ObservationDetails', attribute: 'SubType' }]` → true
   */
  private shouldAutoOpenBuildPath(opt: {
    repeatableNodes?: string[];
    nodePredicates?: XmlPathNodePredicate[];
    path: string;
  }): boolean {
    if ((opt.nodePredicates?.length ?? 0) > 0) {
      return true;
    }
    if ((opt.repeatableNodes?.length ?? 0) > 0) {
      return true;
    }
    const model = buildPathModelFromTemplatePath(opt.path);
    return !!(model && pathModelHasRepeatableStep(model));
  }

  private warnAndStopOnInvalidRuleName(): boolean {
    const control = this.store.ruleForm.get('rulename');
    control?.markAsTouched();
    const message = getRuleNameValidationMessage(this.store.getRuleNameInputValue());
    if (!message) {
      return false;
    }
    this.toastr.warning(message, '', {
      timeOut: 4000,
      positionClass: 'toast-bottom-right',
    });
    return true;
  }
}
